import { useEffect, useMemo, useRef } from 'react';

import lsKey, { type LocalStorageKeyInput } from './base/localStorage.key';

import { createLocalStorageUtils } from './base/localStorage';

import useEvent from '../useEvent';

type SyncInput<T> = {
  enabled?: boolean;
  value: T;
};

type UtilCallbacks<T> = {
  onRemove: () => void;
  onSet: (value: T) => void;
};

const createSyncHook =
  <T>(utils: ReturnType<typeof createLocalStorageUtils<T>>) =>
  (input: SyncInput<T>) => {
    const { enabled = true, value } = input;
    const { remove, set } = utils;

    const lastSyncedValue = useRef(value);

    useEffect(() => {
      if (!enabled || value === lastSyncedValue.current) return;

      const success = value == null ? remove() : set(value);

      if (success) lastSyncedValue.current = value;
    }, [enabled, remove, set, value]);
  };

const useLocalStorageUtils = <T>(keyInput: LocalStorageKeyInput, callbacks?: UtilCallbacks<T>) => {
  const key = lsKey.create(keyInput);

  const handleRemoveCallback = useEvent(callbacks?.onRemove);
  const handleSetCallback = useEvent(callbacks?.onSet);

  const baseUtils = useMemo(() => {
    const utils = createLocalStorageUtils<T>(key);

    const remove = () => {
      const success = utils.remove();

      if (success) handleRemoveCallback();

      return success;
    };

    const set = (value: T) => {
      const success = utils.set(value);

      if (success) handleSetCallback(value);

      return success;
    };

    return {
      ...utils,
      remove,
      set,
    };
  }, [handleRemoveCallback, handleSetCallback, key]);

  return useMemo(
    () => ({
      ...baseUtils,
      key,
      useSync: createSyncHook(baseUtils),
    }),
    [baseUtils, key],
  );
};

export default useLocalStorageUtils;
